<script setup>
import axios from "@axios"
import { reviewLine } from '@/utils/requestApproval'
import { ref, computed, watch, nextTick } from "vue"
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useReservationsStore, useUsersStore } from '@/stores'
import { loadRmStripe } from '@/utils/stripe'
import RequestPaymentInfo from '@core/components/EditReservationDialog/RequestPaymentInfo.vue'

const props = defineProps({
  isActive: {
    type: Boolean,
    required: true,
  },
  selectedRestaurants: {
    type: Array,
    default: () => [],
  },
  selectedRestaurantsNotPreferred: {
    type: Array,
    default: () => [],
  },
  isNotHost: {
    type: Boolean,
    default: false,
  },
  isPreferredAtSelectedRestaurant: {
    type: Boolean,
    default: false,
  },
  selectedRestaurantsMinSpend: {
    type: Array,
    default: () => [],
  },
  selectedRestaurantNames: {
    type: String,
    default: '',
  },
  openedViaAgentHandoff: {
    type: Boolean,
    default: false,
  },
  // When true (reservation within 48h), the cancellation policy starts expanded.
  isWithin48Hours: {
    type: Boolean,
    default: false,
  },
  // RM: hold-amount breakdown + first restaurant name for the "How minimum
  // works" explainer, re-shown on this step so AI-chat members (who open
  // straight here and skip the Spend screen) still see how the hold works.
  pricingBreakdown: {
    type: Object,
    default: null,
  },
  firstRestaurantName: {
    type: String,
    default: '',
  },
})

const router = useRouter()
const usersStore = useUsersStore()
const reservationsStore = useReservationsStore()
const { myUser } = storeToRefs(usersStore)
// confirmedPresence is set on the Host Profile step (the "this is for me / I'll
// be present + member commitment" sign-off); read here to gate Submit.
const { editingReservation, confirmedPresence } = storeToRefs(reservationsStore)

// RM-150: gate the Submit button on the OWNER's membership, not the actor's
// (a personal admin booking on behalf of a Platinum inviter has no
// activeSubscription themselves). Same pattern as EditReservationReviewStep.vue.
// RM-154 follow-up: see EditReservationDialog.vue for the partial-owner-object
// flaw and the truthy-wins rationale. Normalize email comparison, allow
// ObjectId/string _id matching, and truthy-union membership flags for
// self-bookings so a stale owner stub doesn't override myUser's truthy state.
const reservationOwner = computed(() => {
  const owner = editingReservation.value?.owner
  const me = myUser.value
  if (!owner) return me || {}
  if (!owner.email && !owner._id) return me || {}

  const ownerEmail = String(owner.email || '').toLowerCase().trim()
  const meEmail = String(me?.email || '').toLowerCase().trim()
  const isSelfBooking = me && (
    (ownerEmail && ownerEmail === meEmail)
    || (owner._id && me._id && String(owner._id) === String(me._id))
  )

  if (isSelfBooking) {
    return {
      ...me,
      ...owner,
      isPlatinum: !!(owner.isPlatinum || me.isPlatinum),
      isPreferred: !!(owner.isPreferred || me.isPreferred),
      activeSubscription: owner.activeSubscription || me.activeSubscription,
    }
  }

  return owner
})

const paymentMethods = ref([])
const selectedPaymentMethod = ref(null)
const showNewCard = ref(false)
const newCardComplete = ref(false)
const stripe = ref(null)
const cardElement = ref(null)
const elements = ref(null)
const cardElementMounted = ref(false)
const cardElementContainerRef = ref(null)
const processingPayment = ref(false)
const paymentError = ref('')
// RM: set true after a declined card; grays out Submit until the member enters
// a card again (prevents confused repeat-clicks on the same declined card).
const cardDeclined = ref(false)
const saveCardForFuture = ref(true)
// Card-hold authorization sign-off (the financial agreement on this screen).
const agreedToPaymentTerms = ref(false)

// Cancellation policy starts expanded when the reservation is within 48h; @toggle
// keeps the ref in sync so a manual collapse isn't re-opened on re-render.
const cancellationOpen = ref(false)
watch(() => props.isWithin48Hours, value => { cancellationOpen.value = value }, { immediate: true })

const initializeStripe = async () => {
  try {
    if (stripe.value) return

    // RM-313: the key comes from the server that will process the card, so the
    // two are always in the same Stripe mode, whatever the hostname.
    stripe.value = await loadRmStripe()

    if (!stripe.value) {
      throw new Error('Failed to load Stripe')
    }

    elements.value = stripe.value.elements()
  } catch (error) {
    console.error('Failed to initialize Stripe:', error)
    paymentError.value = 'Failed to load payment form. Please refresh the page.'
  }
}

const mountCardElement = async () => {
  if (cardElement.value) {
    try {
      cardElement.value.unmount()
    } catch (e) {
      // Ignore unmount errors (e.g., element was created but never mounted)
    }
    cardElement.value.destroy()
    cardElement.value = null
    cardElementMounted.value = false
  }

  await initializeStripe()

  if (!elements.value) return

  cardElement.value = elements.value.create('card', {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  })

  cardElement.value.on('change', event => {
    newCardComplete.value = event.complete
    // Re-entering / editing card details clears a prior decline so Submit re-enables.
    cardDeclined.value = false
    if (event.error) {
      paymentError.value = event.error.message
    } else {
      paymentError.value = ''
    }
  })

  await nextTick()
  if (cardElementContainerRef.value && !cardElementMounted.value) {
    cardElement.value.mount(cardElementContainerRef.value)
    cardElementMounted.value = true
  }
}

watch(cardElementContainerRef, el => {
  if (el && cardElement.value && !cardElementMounted.value) {
    cardElement.value.mount(el)
    cardElementMounted.value = true
  }
})

const unmountCardElement = () => {
  if (cardElement.value) {
    try {
      cardElement.value.unmount()
    } catch (e) {
      // Ignore unmount errors (e.g., element was created but never mounted)
    }
    cardElement.value.destroy()
    cardElement.value = null
    cardElementMounted.value = false
    newCardComplete.value = false
  }
}

const loadPaymentMethods = async () => {
  try {
    const res = await axios.get('/checkout/payment-methods', {
      params: {
      }
    })
    paymentMethods.value = res.data

    if (paymentMethods.value.length === 0) {
      showNewCard.value = true
      await mountCardElement()
    } else {
      selectedPaymentMethod.value = paymentMethods.value[0].id
      showNewCard.value = false
    }
  } catch (error) {
    console.error('Failed to load payment methods:', error)
    paymentError.value = 'Failed to load payment methods. Please refresh the page.'
  }
}

const handleAddNewCard = async () => {
  showNewCard.value = true
  selectedPaymentMethod.value = null
  newCardComplete.value = false
  cardDeclined.value = false
  paymentError.value = ''
  await mountCardElement()
}

const handleSelectSavedCard = () => {
  unmountCardElement()
  showNewCard.value = false
  cardDeclined.value = false
  paymentError.value = ''
  if (paymentMethods.value.length > 0 && !selectedPaymentMethod.value) {
    selectedPaymentMethod.value = paymentMethods.value[0].id
  }
}

// Selecting a different saved card clears a prior decline.
watch(selectedPaymentMethod, () => {
  cardDeclined.value = false
})

const isPaymentMethodReady = computed(() => {
  if (!myUser.value || myUser.value.userLevel === 'dummy') return true
  if (props.isPreferredAtSelectedRestaurant) return true

  const holdRequired = props.selectedRestaurants.some(restaurant => restaurant.generalReservationHoldRequired)
  if (!holdRequired) return true

  return selectedPaymentMethod.value || (showNewCard.value && newCardComplete.value)
})

const handleSubmitRequest = async () => {
  if (processingPayment.value) return

  processingPayment.value = true
  paymentError.value = ''

  try {
    const holdRequired = !props.isPreferredAtSelectedRestaurant
      && props.selectedRestaurants.some(restaurant => restaurant.generalReservationHoldRequired)

    let paymentMethodId = null

    if (holdRequired) {
      if (showNewCard.value && !selectedPaymentMethod.value) {
        if (!cardElementMounted.value || !cardElement.value) {
          throw new Error('Card form is not ready. Please enter your card details.')
        }

        const { error, paymentMethod } = await stripe.value.createPaymentMethod({
          type: 'card',
          card: cardElement.value,
        })

        if (error) {
          throw new Error(error.message)
        }

        paymentMethodId = paymentMethod.id

        if (saveCardForFuture.value) {
          await axios.post('/checkout/save-payment-method', {
            paymentMethodId,
            jwt: JSON.parse(localStorage.getItem('jwt'))
          })
        }
      } else {
        paymentMethodId = selectedPaymentMethod.value
      }

      if (!paymentMethodId) {
        throw new Error('Please select a payment method.')
      }
    }

    editingReservation.value.stripeRestaurantHoldData = props.selectedRestaurants.map(restaurant => {
      return restaurant.holdData
    }).sort((a, b) => {
      if(a.restaurantName < b.restaurantName) { return -1 }
      if(a.restaurantName > b.restaurantName) { return 1 }

      return 0
    })

    const maxHoldAmount = Math.max(...props.selectedRestaurants.map(restaurant => {
      const amount = Number(restaurant.holdData?.holdAmountDollars)

      return Number.isFinite(amount) ? amount : 0
    }))

    // Never send an unusable hold amount to the server. `holdAmountDollars` is
    // `partySize * minSpendPerPerson` (+ tax/gratuity), so a request with no
    // guaranteed spend makes it NaN, which serializes to null and comes back a
    // 400. Members hit exactly that: the AI handoff opens this dialog at the
    // Review step and skips step 5p's spend gate, so they arrived here with no
    // spend on the request and were told to "refresh and try again" — advice
    // that can never work, because the missing value is on the reservation.
    // Spend is optional for Preferred Members (their hold is bypassed
    // server-side), so this mirrors `handleSpendNext`'s `spendRequired`.
    const spendRequired = !props.isPreferredAtSelectedRestaurant
    if (spendRequired && !(maxHoldAmount > 0)) {
      throw new Error('This request is missing your guaranteed spend, which is needed to hold the table. Go back and choose a per-person spend amount, then submit.')
    }

    editingReservation.value.stripeHoldMaxAmount = maxHoldAmount

    await reservationsStore.confirmEditingReservation({
      email: myUser.value.email,
      firstName: myUser.value.firstName,
      lastName: myUser.value.lastName
    }, paymentMethodId, saveCardForFuture.value)

    router.push({ path: '/bookings' })
  } catch (error) {
    console.error('Submit request failed:', error)
    paymentError.value = error.response?.data?.error || error.response?.data?.message || error.message || 'An error occurred. Please try again.'
    // Declined card → gray out Submit until the member enters a card again.
    if (/declin/i.test(paymentError.value)) {
      cardDeclined.value = true
    }
  } finally {
    processingPayment.value = false
  }
}

const reset = () => {
  unmountCardElement()
  paymentMethods.value = []
  selectedPaymentMethod.value = null
  showNewCard.value = false
  newCardComplete.value = false
  processingPayment.value = false
  paymentError.value = ''
  cardDeclined.value = false
  saveCardForFuture.value = true
  agreedToPaymentTerms.value = false
}

// RM-359: will RM review this request before it's sent, and for which
// restaurants? (the server decides; yes/no and the names only)
const approvalNeeded = ref(false)
const approvalRestaurants = ref([])
const checkApproval = async () => {
  const id = editingReservation.value?._id
  if (!id) return
  try {
    const { data } = await axios.get(`/reservation/${id}/approval-check`)
    approvalNeeded.value = !!data?.approvalNeeded
    approvalRestaurants.value = Array.isArray(data?.restaurants) ? data.restaurants : []
  } catch (error) {
    approvalNeeded.value = false
    approvalRestaurants.value = []
  }
}

watch(() => props.isActive, async (newActive, oldActive) => {
  if (newActive) checkApproval()
  if (newActive && !oldActive) {
    // Load payment methods on entering the step.
    // `immediate: true` covers the case where this child mounts lazily inside
    // VWindowItem with isActive already true (oldActive === undefined).
    // Always load when opened via agent handoff — restaurant placeholder may
    // not have generalReservationHoldRequired loaded yet. Preferred members
    // never see this step, so skip the load.
    const holdRequired = !props.isPreferredAtSelectedRestaurant && (
      props.openedViaAgentHandoff ||
      props.selectedRestaurants.some(restaurant => restaurant.generalReservationHoldRequired)
    )
    if (holdRequired) {
      await loadPaymentMethods()
    }
  }

  if (oldActive && !newActive) {
    if (cardElementMounted.value) {
      unmountCardElement()
    }
  }
}, { immediate: true })

defineExpose({ reset })
</script>

<template>
  <div class="mb-8 tab-content mx-4">
    <div
      v-if="!isPreferredAtSelectedRestaurant"
      class="guaranteed-spend-section"
    >
      <div
        v-if="myUser && myUser.userLevel !== 'dummy'"
        class="credit-card-hold-section"
      >
        <div class="credit-card-hold-header">
          <VIcon
            icon="mdi-credit-card-outline"
            size="20"
          />
          <span class="credit-card-hold-title">Credit card for hold for guaranteed F&amp;B spend</span>
        </div>
        <div class="credit-card-hold-body">
          <VSelect
            v-if="paymentMethods.length > 0 && !showNewCard"
            v-model="selectedPaymentMethod"
            :items="paymentMethods.map(pm => ({
              title: `${pm.card.brand.toUpperCase()} •••• ${pm.card.last4}`,
              value: pm.id
            }))"
            label="Select a card"
            hide-details
          >
            <template #prepend-item>
              <VListItem @click="handleAddNewCard">
                <template #prepend>
                  <VIcon icon="mdi-plus" />
                </template>
                <VListItemTitle>{{ $t('reservation.payment.addNewCard') }}</VListItemTitle>
              </VListItem>
              <VDivider class="my-2" />
            </template>
          </VSelect>

          <div v-if="showNewCard || paymentMethods.length === 0">
            <div
              ref="cardElementContainerRef"
              class="card-element-container"
            />
            <div
              v-if="paymentError"
              class="text-error payment-error-text mt-1"
            >
              {{ paymentError }}
            </div>
            <VCheckbox
              v-model="saveCardForFuture"
              :label="$t('reservation.payment.saveCardFuture')"
              density="compact"
              hide-details
              class="mt-2"
            />
            <VBtn
              v-if="paymentMethods.length > 0"
              variant="text"
              color="primary"
              size="small"
              class="mt-2"
              @click="handleSelectSavedCard"
            >
              Use Saved Card
            </VBtn>
          </div>
        </div>
      </div>

      <RequestPaymentInfo
        class="mt-4"
        :pricing-breakdown="pricingBreakdown"
        :first-restaurant-name="firstRestaurantName"
      />

      <details
        :open="cancellationOpen"
        class="policy-section mt-4"
        @toggle="cancellationOpen = $event.target.open"
      >
        <summary
          class="policy-header"
          style="cursor: pointer; list-style: none;"
        >
          <VIcon
            icon="mdi-alert-circle-outline"
            size="20"
            class="policy-icon"
          />
          <span class="policy-title">{{ $t('reservation.payment.cancellationPolicy') }}</span>
          <span style="margin-left: auto; font-size: 0.7rem; color: #666;">▸</span>
        </summary>
        <div style="margin-top: 8px;">
          <p class="policy-text">
            {{ $t('reservation.payment.fullRefund') }}
          </p>
          <p class="policy-penalty">
            {{ $t('reservation.payment.cancellationPenalty') }}
          </p>
          <p class="policy-cancel-anytime">
            Cancel any time prior to acceptance via app/website
          </p>
        </div>
      </details>
    </div>

    <!-- Card-hold authorization sign-off -->
    <div
      v-if="!isPreferredAtSelectedRestaurant && editingReservation?.status == 'Draft' && reservationOwner.activeSubscription && !(selectedRestaurantsNotPreferred && !reservationOwner.isPlatinum)"
      class="text-center mt-4"
    >
      <div
        class="confirm-hold-card"
        :class="{ 'confirm-hold-card--active': agreedToPaymentTerms }"
        @click="agreedToPaymentTerms = !agreedToPaymentTerms"
      >
        <VCheckbox
          v-model="agreedToPaymentTerms"
          density="compact"
          color="primary-dark"
          hide-details
          class="confirm-hold-checkbox"
          @click.stop
        />
        <!-- Kapil's wording, 2026-09-30 (RM-360); the text review says the same (lib/agent/channels/submitOverText) -->
        <span class="confirm-hold-label ml-2">
          <template v-if="isNotHost">
            I authorize on behalf of {{ editingReservation.owner?.firstName }} the credit card hold &amp; agree to the cancellation + guaranteed spend policy
          </template>
          <template v-else>
            I authorize the credit card hold &amp; agree to the cancellation + guaranteed spend policy
          </template>
        </span>
      </div>
    </div>

    <VCol
      cols="12"
      class="d-flex flex-wrap justify-center gap-4"
    >
      <VSpacer />
      <div
        v-if="editingReservation?.status == 'Draft' && reservationOwner.activeSubscription && !(selectedRestaurantsNotPreferred && !reservationOwner.isPlatinum)"
        class="w-100"
      >
        <!-- RM-360 (Kapil 9/30): the parent's "See what {restaurant} will receive", right above Submit -->
        <slot name="before-submit" />
        <VBtn
          :disabled="selectedRestaurantsMinSpend?.length == 0 || processingPayment || !isPaymentMethodReady || (!isPreferredAtSelectedRestaurant && !agreedToPaymentTerms) || !confirmedPresence || cardDeclined"
          :loading="processingPayment"
          color="primary"
          size="large"
          block
          class="submit-request-btn"
          @click="handleSubmitRequest"
        >
          {{ $t('reservation.actions.submit') }}
        </VBtn>
        <div
          v-if="paymentError"
          class="mt-2 text-error payment-error-text text-center"
        >
          {{ paymentError }}
        </div>
        <div
          v-if="cardDeclined"
          class="mt-1 payment-error-help text-center"
        >
          Please either try a different credit card or reach out to your credit card issuer &amp; ask them to approve holds for RecommendMe Inc. (RM's legal name)
        </div>
        <div class="submit-footer-text">
          <!-- RM-359: never "instantly sent" about a request RM reviews first -->
          <template v-if="approvalNeeded">
            {{ approvalRestaurants.length ? reviewLine(approvalRestaurants) : `RM reviews this request before it's sent to ${selectedRestaurantNames}` }}
          </template>
          <template v-else>
            Request will be instantly sent to {{ selectedRestaurantNames }}
          </template>
        </div>
      </div>
      <VSpacer />
    </VCol>
  </div>
</template>

<style lang="scss" scoped>
$primary-teal: var(--ds-brand-primary-dark);
$text-dark: var(--ds-text-dark);
$text-medium: var(--ds-text-medium);
$text-light: var(--ds-text-light);
$border-light: var(--ds-border-light);
$bg-white: var(--ds-surface-card);

.policy-section {
  background: $bg-white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid $border-light;
}

.policy-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.policy-icon {
  color: $text-medium;
}

.policy-title {
  font-size: 16px;
  font-weight: 500;
  color: $text-dark;
}

.policy-text {
  font-size: 16px;
  font-weight: 700;
  color: $text-dark;
  margin: 0 0 4px 0;
  padding-left: 28px;
  line-height: 1.4;

  strong {
    font-weight: 700;
  }
}

.policy-penalty {
  font-size: 16px;
  color: $text-light;
  margin: 0;
  padding-left: 28px;
  line-height: 1.4;
}

.policy-cancel-anytime {
  font-size: 16px;
  color: $text-light;
  margin: 8px 0 0 0;
  padding-left: 28px;
  line-height: 1.4;
}

.credit-card-hold-section {
  background: $bg-white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid $border-light;
}

.credit-card-hold-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.credit-card-hold-title {
  font-size: 16px;
  font-weight: 500;
  color: $text-dark;
}

.credit-card-hold-body {
  width: 100%;
}

.card-element-container {
  border: 1px solid $border-light;
  border-radius: 8px;
  padding: 14px;
  background: $bg-white;

  &:focus-within {
    border-color: $primary-teal;
    box-shadow: 0 0 0 1px $primary-teal;
  }
}

details.policy-section {
  summary::-webkit-details-marker {
    display: none;
  }

  &[open] summary span:last-child {
    transform: rotate(90deg);
    display: inline-block;
  }
}

.confirm-hold-card {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  background: $bg-white;
  border: 1px solid $border-light;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: border-color 0.15s ease;
  text-align: left;
}

.confirm-hold-card--active {
  border-color: var(--ds-brand-primary-dark, $primary-teal);
}

.confirm-hold-checkbox {
  flex-shrink: 0;
}

.confirm-hold-checkbox :deep(.v-selection-control) {
  min-height: auto;
}

.confirm-hold-checkbox :deep(.v-selection-control__input i) {
  border-radius: 6px;
}

.confirm-hold-label {
  font-size: 14px;
  color: $text-dark;
  line-height: 1.4;
}

.member-commitment-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  cursor: pointer;
}

.submit-request-btn {
  font-weight: 700;
  letter-spacing: 0.02em;
  max-width: 320px;
  margin: 0 auto;
  display: block;
}

.submit-footer-text {
  margin-top: 12px;
  text-align: center;
  font-size: 12px;
  color: #999;
  line-height: 1.5;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}

// Declined-card / payment error messaging. 16px per spec so it reads clearly
// (and matches the iOS no-zoom input baseline); the help line below it is
// supportive metadata at 14px in a muted tone.
.payment-error-text {
  font-size: 16px;
  line-height: 1.4;
}

.payment-error-help {
  font-size: 14px;
  color: $text-medium;
  line-height: 1.4;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}
</style>
